import  { Injectable } from '@angular/core';

import { map, merge, Observable, tap } from 'rxjs';

import {
  FloorPlanItem,
  ObjectAttributes,
  SectionAttributes,
  TableAttributes,
  FloorPlanTable,
  FloorPlanObject,
  FloorPlanSection,
  GroupedItems,
} from '@shared/floor-plan/models';
import { WebSocketEventsEnum } from '@shared/websocket';
import { FloorPlanItemType } from '@shared/floor-plan/enums';
import {
  FloorPlanItemQuery,
  FloorPlanItemStore,
} from './floor-plan-item-state';
import { FloorPlanItemApiService } from './floor-plan-item-api.service';
import { SocketService } from '@tablesready/frontend-store';

@Injectable({ providedIn: 'root' })
export class FloorPlanItemService {
  constructor(
    private readonly query: FloorPlanItemQuery,
    private readonly store: FloorPlanItemStore,
    private readonly floorPlanItemApiService: FloorPlanItemApiService,
    private readonly socketService: SocketService,
  ) { }

  load(id: string): Observable<FloorPlanItem[]> {
    return this.floorPlanItemApiService.getListForId(id)
      .pipe(tap(floorPlans => this.store.set(floorPlans)));
  }

  reset() {
    this.store.reset();
  }

  selectRaw(): Observable<FloorPlanItem[]> {
    return this.query.select()
      .pipe(map((state) => {
        const items: FloorPlanItem[] = [];
        Object.entries(state.entities).forEach(([, value]) => {
          items.push(value);
        });

        return items;
      }));
  }

  selectGrouped(): Observable<GroupedItems> {
    return this.selectRaw()
      .pipe(
        map(items => {
          const result: GroupedItems = {
            tables: [],
            objects: [],
            sections: [],
          };

          for (let i = 0; i < items.length; i++) {
            const {id, floorPlanId, type, name, attributes} = items[i];
            switch (type) {
              case FloorPlanItemType.table:
                result.tables.push(new FloorPlanTable(id, floorPlanId, name, attributes as TableAttributes));
                break;

              case FloorPlanItemType.object:
                result.objects.push(new FloorPlanObject(id, floorPlanId, name, attributes as ObjectAttributes));
                break;

              case FloorPlanItemType.section:
                result.sections.push(new FloorPlanSection(id, floorPlanId, name, attributes as SectionAttributes));
                break;
            }
          }

          return result;
        }),
      );
  }

  listenToChanges() {
    return merge(
      this.socketService
        .getEventStream<FloorPlanItem[]>(WebSocketEventsEnum.floorPlanItemCreated)
        .pipe(tap(floorPlan => this.store.add(floorPlan))),
      this.socketService
        .getEventStream<FloorPlanItem[]>(WebSocketEventsEnum.floorPlanItemUpdated)
        .pipe(tap(reply => {
          for (let i = 0; i < reply.length; i++) {
            this.store.update(reply[i].id, reply[i]);
          }
        })),
      this.socketService
        .getEventStream<string[]>(WebSocketEventsEnum.floorPlanItemDeleted)
        .pipe(tap(ids => this.store.remove(ids))),
      this.socketService
        .getEventStream<string[]>(WebSocketEventsEnum.floorPlanDeleted)
        .pipe(tap(ids => this.store.remove(({floorPlanId}) => ids.includes(floorPlanId)))),
    );
  }
}
